/* global Phone */
import React, { createContext, useState, useEffect } from "react";
import { getCallToken, getUserId } from "./tokens";

const SCRIPT_SRC =
  "https://www.teltel.io/v2/static/webphone/latest/plugin/webphone.js";

const WebPhoneContext = createContext({
  phone: null,
  resetPhone: () => {},
});

function phoneConstructor() {
  return typeof window.Phone === "function" ? window.Phone : null;
}

function initPhone(token) {
  if (window.__teltelPhone) {
    return window.__teltelPhone;
  }
  const Ctor = phoneConstructor();
  if (!Ctor) {
    return null;
  }
  // TelTel docs: the floating widget is created by `new Phone(user-key)` with
  // no extra options. Extra constructor args have been observed to prevent
  // the dock from mounting.
  const instance = new Ctor(token);
  window.__teltelPhone = instance;
  return instance;
}

function ensureWebphoneScript() {
  const selector = 'script[src="' + SCRIPT_SRC + '"]';
  if (document.querySelector(selector)) {
    return;
  }
  const script = document.createElement("script");
  script.src = SCRIPT_SRC;
  script.async = true;
  document.body.appendChild(script);
}

const WebPhoneProvider = ({ children, useProvider }) => {
  const [phone, setPhone] = useState(() => window.__teltelPhone || null);
  const key = getUserId();

  useEffect(() => {
    if (!useProvider || key == null || key == false || key == "") {
      return;
    }

    // Original gate: empty string skips. null still attempted `new Phone(null)`.
    if (getCallToken() == "") {
      return;
    }

    const token = getCallToken();
    ensureWebphoneScript();

    let unmounted = false;
    const started = Date.now();
    const id = setInterval(() => {
      const instance = initPhone(token);
      if (instance) {
        clearInterval(id);
        if (!unmounted) {
          setPhone(instance);
        }
        return;
      }
      if (Date.now() - started > 15000) {
        clearInterval(id);
      }
    }, 200);

    return () => {
      unmounted = true;
      clearInterval(id);
    };
  }, [useProvider, key]);

  const resetPhone = () => {
    window.__teltelPhone = null;
    setPhone(null);
  };

  return (
    <WebPhoneContext.Provider value={{ phone, resetPhone }}>
      {children}
    </WebPhoneContext.Provider>
  );
};

export { WebPhoneContext, WebPhoneProvider };
